import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { TRANSLATIONS, LANGUAGE_OPTIONS, loadPageOverlays } from '../i18n/translations';
import notificationService from '../services/notificationService';

/**
 * Phase 18h — client-side i18n.
 *
 * Single source of truth for the app's current language:
 *   * ``localStorage`` holds the last-picked code so the app renders
 *     instantly (before any API round-trip).
 *   * When the user is authenticated we GET the notification profile
 *     and reconcile — the server wins over the cached value so the
 *     user sees the same language the backend uses when rendering
 *     their emails.
 *   * ``setLanguage`` PATCHes the profile, updates local state, and
 *     broadcasts the change to every subscribing component.
 *
 * The dictionaries live in ``src/i18n/translations.js`` — missing
 * keys fall back to EN, so a half-translated string never blocks
 * the UI.
 */

const LS_KEY = 'ws:language';
const DEFAULT_LANG = 'EN';

const LanguageContext = createContext({
  language: DEFAULT_LANG,
  setLanguage: () => {},
  t: (key, fallback) => fallback ?? key,
  languages: LANGUAGE_OPTIONS,
  profile: null,
});

const readInitial = () => {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && TRANSLATIONS[stored]) return stored;
  } catch { /* localStorage disabled (private mode, old Safari) */ }
  return DEFAULT_LANG;
};

// Tiny templating helper — replaces ``{name}`` tokens with the
// matching value from ``vars``. Keeps translations free of JSX
// concatenation so the string renders RTL-safely in future.
const interpolate = (template, vars) => {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (
    vars[k] == null ? `{${k}}` : String(vars[k])
  ));
};

export const LanguageProvider = ({ children }) => {
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const [language, setLanguageState] = useState(readInitial);
  const [profile, setProfile] = useState(null);
  // Per-page translation overlay — code-split, loaded after first paint
  // so it stays out of the main bundle. Until it arrives, t() uses the
  // inline English fallbacks (so the UI is never blocked).
  const [pageOverlays, setPageOverlays] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadPageOverlays()
      .then((o) => { if (!cancelled) setPageOverlays(o); })
      .catch(() => { /* non-fatal — inline English fallbacks remain */ });
    return () => { cancelled = true; };
  }, []);

  // Reconcile with the server on login so a user who changed their
  // language on mobile sees the same copy on the web.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) return undefined;
    (async () => {
      try {
        const { data } = await notificationService.getProfile();
        if (cancelled || !data) return;
        setProfile(data);
        const serverLang = (data.preferred_language || '').toUpperCase();
        if (serverLang && TRANSLATIONS[serverLang]) {
          setLanguageState(serverLang);
          try { localStorage.setItem(LS_KEY, serverLang); } catch { /* ignore */ }
        }
      } catch {
        // Profile fetch failed — we keep whatever localStorage had.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Public setter — optimistic local update + best-effort server
  // persist. Failure surfaces to the caller via the returned
  // Promise so the UI can toast an error.
  const setLanguage = useCallback(async (code) => {
    const upper = (code || '').toUpperCase();
    if (!TRANSLATIONS[upper]) throw new Error(`unsupported_language:${code}`);
    setLanguageState(upper);
    try { localStorage.setItem(LS_KEY, upper); } catch { /* ignore */ }
    if (isAuthenticated) {
      const { data } = await notificationService.updateProfile({
        preferred_language: upper,
      });
      setProfile(data);
    }
    return upper;
  }, [isAuthenticated]);

  const t = useCallback((key, fallbackOrVars, maybeVars) => {
    // Support both call shapes:
    //   t('key')
    //   t('key', 'Fallback string')
    //   t('key', { name: 'Alice' })
    //   t('key', 'Hello {name}', { name: 'Alice' })
    let fallback;
    let vars;
    if (typeof fallbackOrVars === 'string') {
      fallback = fallbackOrVars;
      vars = maybeVars;
    } else if (fallbackOrVars && typeof fallbackOrVars === 'object') {
      vars = fallbackOrVars;
    }

    // Precedence: page overlay (current lang → EN) → chrome dict
    // (current lang → EN) → the call site's inline English fallback → key.
    const oLang = (pageOverlays && pageOverlays[language]) || null;
    const oEn = (pageOverlays && pageOverlays[DEFAULT_LANG]) || null;
    const dict = TRANSLATIONS[language] || TRANSLATIONS[DEFAULT_LANG];
    const enDict = TRANSLATIONS[DEFAULT_LANG];
    const raw =
      (oLang && oLang[key]) ??
      (oEn && oEn[key]) ??
      dict[key] ??
      enDict[key] ??
      fallback ??
      key;
    return interpolate(raw, vars);
  }, [language, pageOverlays]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    languages: LANGUAGE_OPTIONS,
    profile,
  }), [language, setLanguage, t, profile]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Consumer hook — the thin surface most components will touch.
export const useTranslation = () => useContext(LanguageContext);

export default LanguageContext;
